import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { DirectionService } from '../../direction.service';
import { AuthService } from 'src/app/auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ClustersService } from 'src/app/main-section/choose-branch/clusters.service';
import { DialogService } from 'src/app/dialog/dialog.service';

/** Step id: 1 = בחירת מערכת, 2 = בחירת מדפסת, 3 = בחירת תכנה, 4 = בחירת נייר, 5 = הגדרת מידות, 6 = גיליון חדש/קובץ קיים, 7 = הוראות פתיחה, 8 = שוליים, 9 = Photoshop, 10 = מידה 250 ס"מ, 11 = איך לשמור את הקובץ, 12 = PDF דרך Photoshop, 13 = מה לבדוק לפני הדפסה */
export type EditInstructionsStepId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;
export type EditInstructionsSystemType = 'plotter' | 'express' | 'pics';

export type EditInstructionsSoftwareType =
  | 'illustrator'
  | 'photoshop'
  | 'indesign'
  | 'autocad'
  | 'canva'
  | 'powerpoint'
  | 'revit'
  | 'word'
  | 'other';

@Component({
  selector: 'app-edit-instructions',
  templateUrl: './edit-instructions.component.html',
  styleUrls: ['./edit-instructions.component.css'],
  host: { class: 'fill-screen' },
})
export class EditInstructionsComponent implements OnInit, OnDestroy, AfterViewInit {
  isRTL = true;
  isDarkMode = false;
  private directionSubscription: Subscription;
  private rolesSubscription: Subscription;
  private userNameSubscription: Subscription;
  roles: string[] = [];
  @ViewChild('scrollContainer') scrollContainerRef: ElementRef<HTMLElement> | null = null;
  private lastViewportHeight = 0;
  /** השלבים מוצגים תמיד – גם בלי תשובה מהשרת (סניפים/קלסטרים) */
  isSU = true;
  /** שם המשתמש – להצגת "צור הזמנה חדשה" או פתיחת חלונית התחברות */
  userName = '';

  /** רשימת שלבים (ארדקודד) – ערכים חשובים לכל שלב */
  readonly steps: { id: EditInstructionsStepId; titleKey: string }[] = [
    { id: 1, titleKey: 'edit-instructions.step-1-title' },
    { id: 2, titleKey: 'edit-instructions.step-2-title' },
    { id: 3, titleKey: 'edit-instructions.step-3-title' },
    { id: 4, titleKey: 'edit-instructions.step-4-title' },
    { id: 5, titleKey: 'edit-instructions.step-5-title' },
    { id: 6, titleKey: 'edit-instructions.step-6-title' },
    { id: 7, titleKey: 'edit-instructions.step-7-title' },
    { id: 8, titleKey: 'edit-instructions.step-8-title' },
    { id: 9, titleKey: 'edit-instructions.step-9-title' },
    { id: 10, titleKey: 'edit-instructions.step-10-title' },
    { id: 11, titleKey: 'edit-instructions.step-12-title' },
    { id: 12, titleKey: 'edit-instructions.step-11-title' },
    { id: 13, titleKey: 'edit-instructions.step-13-title' },
  ];

  /** אפשרויות לשלב 1 – סוג מערכת */
  readonly systemOptions: { value: EditInstructionsSystemType; labelKey: string; icon: string; explanationKey: string }[] = [
    { value: 'plotter', labelKey: 'choose-system.title-short-short-plotter', icon: 'assets/images/plotter-icon.svg', explanationKey: 'q-and-a.1.content-plotter-1' },
    { value: 'express', labelKey: 'choose-system.title-short-short-express', icon: 'assets/images/express-icon.svg', explanationKey: 'q-and-a.1.content-express-1' },
    { value: 'pics', labelKey: 'choose-system.title-short-short-pics', icon: 'assets/images/pics-icon.svg', explanationKey: 'q-and-a.1.content-pics-1' },
  ];

  /** אפשרויות לשלב 3 – תכנת עריכה (שמורות באובייקט השאלה). label = hardcoded, labelKey = מפתח תרגום */
  readonly softwareOptions: { value: EditInstructionsSoftwareType; label?: string; labelKey?: string; icon: string }[] = [
    { value: 'illustrator', label: 'Illustrator', icon: 'assets/images/upload-file-icon.svg' },
    { value: 'photoshop', label: 'Photoshop', icon: 'assets/images/upload-file-icon.svg' },
    { value: 'indesign', label: 'Indesign', icon: 'assets/images/upload-file-icon.svg' },
    { value: 'autocad', label: 'AutoCAD', icon: 'assets/images/upload-file-icon.svg' },
    { value: 'canva', label: 'Canva', icon: 'assets/images/upload-file-icon.svg' },
    { value: 'powerpoint', label: 'PowerPoint', icon: 'assets/images/upload-file-icon.svg' },
    { value: 'revit', label: 'Revit', icon: 'assets/images/upload-file-icon.svg' },
    { value: 'word', label: 'Word', icon: 'assets/images/upload-file-icon.svg' },
    { value: 'other', labelKey: 'edit-instructions.software.other', icon: 'assets/images/upload-file-icon.svg' },
  ];

  /** איזו הסבר מוצג (לחיצה על i-explain) */
  expandedExplainOption: EditInstructionsSystemType | null = null;
  /** איזה הסבר נייר מוצג (לחיצה על i-explain) */
  expandedPaperExplain: string | null = null;
  /** האם הסבר מתקדם על Anti-aliased פתוח (שאלה 12) */
  expandedAntiAliasedExplain = false;
  /** האם הסבר "נתקלתי בבעיה" פתוח (שאלה 7) */
  expandedStep7ProblemExplain = false;
  /** האם הסבר "לא הבנתי" פתוח (שאלה 8) */
  expandedStep8ProblemExplain = false;
  /** האם הסבר "נתקלתי בבעיה" פתוח (שאלה 11) */
  expandedStep11ProblemExplain = false;
  /** האם הסבר "נתקלתי בבעיה" פתוח (שאלה 12) */
  expandedStep12ProblemExplain = false;
  /** האם הסבר "נתקלתי בבעיה" פתוח (שאלה 13) */
  expandedStep13ProblemExplain = false;

  /** באיזה שאלה המשתמש – מתחיל ב-1 */
  currentStep: EditInstructionsStepId = 1;
  /** בחירת מערכת בשלב 1 */
  selectedSystemType: EditInstructionsSystemType | null = null;

  isLoading = false;
  /** true רק אחרי תשובה מוצלחת מהשרת – כל עוד לא הגיע המידע (טוען או שגיאה) מציגים ספינר */
  clustersLoadCompleted = false;
  clusters: any[] = [];
  selectedClusterSerialName: string | null = null;
  /** בחירת תכנה בשלב 3 */
  selectedSoftware: EditInstructionsSoftwareType | null = null;
  /** בחירת נייר בשלב 4 */
  selectedPaperType: string | null = null;
  /** בשלב 5: האם המשתמש יודע להגדיר מידות – true=כן, false=לא */
  selectedStep5KnowsDimensions: boolean | null = null;
  /** בשלב 6: גיליון חדש או קובץ קיים – 'new'=גיליון חדש, 'existing'=קובץ קיים, 'skipped'=דילגתי (אוטומטי כשבוחרים "כן" ב-5) */
  selectedStep6FileType: 'new' | 'existing' | 'skipped' | null = null;
  /** בשלב 7: עד שלב זה הסתדרת? – true=כן, false=נתקלתי בבעיה, 'skipped'=דילגת (אוטומטי כשבוחרים "כן" ב-5) */
  selectedStep7Ok: boolean | 'skipped' | null = null;
  /** בשלב 8: האם ההסבר ברור? – true=כן, false=לא הבנתי (מוצג רק כשלא נבחר סוג הדפסה או נבחר פלוטר) */
  selectedStep8Ok: boolean | null = null;
  /** בשלב 9: האם יש Photoshop – true=כן, false=לא (מוצג רק כשלא נבחרה תכנה או נבחרו Illustrator/Indesign/AutoCAD) */
  selectedStep9HasPhotoshop: boolean | null = null;
  /** בשלב 10: האם מידה אחת > 250 ס"מ – true=כן, false=שתי המידות < 250 (מוצג רק כשמתקיימים תנאי פלוטר, תכנה, ו-Photoshop) */
  selectedStep10Size250: boolean | null = null;
  /** בשלב 11: הצלחתי להעלות / נתקלתי בבעיה (איך לשמור את הקובץ) */
  selectedStep11Ok: boolean | null = null;
  /** בשלב 12: עד שלב זה הסתדרת? – true=כן, false=נתקלתי בבעיה (PDF דרך Photoshop, מוצג רק כשמתקיימים תנאי isViaPhotoshop) */
  selectedStep12Ok: boolean | null = null;

  get isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  /** האפשרות הנבחרת בשלב 1 (לצורך הצגת התשובה בכותרת) */
  get selectedSystemOption() {
    if (!this.selectedSystemType) return null;
    return this.systemOptions.find((o) => o.value === this.selectedSystemType) ?? null;
  }

  /** קלסטרים מסוננים לשלב 2 – רק כאלה שיש בהם את סוג המדפסת הרלוונטי */
  get filteredClustersForStep2(): any[] {
    if (!this.selectedSystemType) return this.clusters;
    return this.clusters.filter((cl) => {
      const hasPlotter = cl.plotter && cl.plotter.length > 0;
      const hasExpress = cl.express && cl.express.length > 0 && cl.express.some((e: any) => !e.isPicsPrinter);
      const hasPics = cl.express && cl.express.length > 0 && cl.express.some((e: any) => e.isPicsPrinter);
      if (this.selectedSystemType === 'plotter') return hasPlotter;
      if (this.selectedSystemType === 'express') return hasExpress;
      if (this.selectedSystemType === 'pics') return hasPics;
      return true;
    });
  }

  /** האפשרות הנבחרת בשלב 3 (לצורך הצגת התשובה בכותרת) */
  get selectedSoftwareOption() {
    if (!this.selectedSoftware) return null;
    return this.softwareOptions.find((o) => o.value === this.selectedSoftware) ?? null;
  }

  /** הקלאסטר הנבחר */
  get selectedCluster(): any {
    if (!this.selectedClusterSerialName) return null;
    return this.clusters.find((c) => c.serial_name === this.selectedClusterSerialName) ?? null;
  }

  /** הסניף הנבחר (branch) – plotter או express מהקלאסטר */
  get selectedBranch(): any {
    if (!this.selectedCluster || !this.selectedSystemType) return null;
    if (this.selectedSystemType === 'plotter') {
      return this.selectedCluster.plotter?.[0] ?? null;
    }
    if (this.selectedSystemType === 'express') {
      return (this.selectedCluster.express || []).find((e: any) => !e?.isPicsPrinter) ?? this.selectedCluster.express?.[0] ?? null;
    }
    if (this.selectedSystemType === 'pics') {
      return (this.selectedCluster.express || []).find((e: any) => !!e?.isPicsPrinter) ?? this.selectedCluster.express?.[0] ?? null;
    }
    return null;
  }

  /** סוגי נייר זמינים בשלב 4 – מ־branch.paperTypes (מבנה ה-API של clusters/foredit). מאחד מכל ה-branches התואמים. */
  get availablePaperTypes(): any[] {
    if (!this.selectedCluster || !this.selectedSystemType) return [];
    const branches = this.selectedSystemType === 'plotter'
      ? (this.selectedCluster.plotter || [])
      : (this.selectedCluster.express || []).filter((e: any) =>
          this.selectedSystemType === 'pics' ? !!e?.isPicsPrinter : !e?.isPicsPrinter
        );
    const seen = new Set<string>();
    const result: any[] = [];
    for (const b of branches) {
      for (const p of b?.paperTypes || []) {
        const key = this.getPaperTypeName(p);
        if (key && !seen.has(key)) {
          seen.add(key);
          result.push(p);
        }
      }
    }
    return result;
  }

  /** שם הנייר שנבחר להצגה */
  get selectedPaperDisplayName(): string {
    if (!this.selectedPaperType) return '';
    const key = 'paper-type.name.' + this.selectedPaperType;
    const t = this.translate.instant(key);
    return t !== key ? t : this.selectedPaperType;
  }

  /** אובייקט הנייר שנבחר (לצורך מידות) */
  get selectedPaperObject(): any {
    if (!this.selectedPaperType) return null;
    return this.availablePaperTypes.find((p) => this.getPaperTypeName(p) === this.selectedPaperType) ?? null;
  }

  /** טקסט מידות לפלוטר: רוחב בס"מ */
  get step5PlotterWidthText(): string {
    const paper = this.selectedPaperObject;
    if (!paper || !paper.paperWidth || this.selectedSystemType !== 'plotter') return '';
    const cm = this.translate.instant('printing-table.dimensions-cm');
    const paperName = this.selectedPaperDisplayName;
    const width = (paper.paperWidth / 1000).toFixed(2);
    return this.translate.instant('edit-instructions.step-5-plotter-width', { paperName, width, cm });
  }

  /** רוחב × גובה בס"מ לאקספרס/תמונות – מהנייר או ממיפוי תמונות S/M/L */
  private getExpressPaperWidthHeightCm(): { width: string; height: string } | null {
    if (this.selectedSystemType === 'plotter') return null;
    const paper = this.selectedPaperObject;
    if (!paper) return null;
    if (paper.paperWidth != null) {
      const width = (Number(paper.paperWidth) / 1000).toFixed(1);
      const height = paper.paperHeight != null ? (Number(paper.paperHeight) / 1000).toFixed(1) : '-';
      return { width, height };
    }
    if (this.selectedSystemType === 'pics' && this.selectedPaperType) {
      const dims = EditInstructionsComponent.PICS_DIMENSIONS_CM[this.selectedPaperType];
      if (dims) return { width: dims.width.toFixed(2), height: dims.height.toFixed(2) };
    }
    return null;
  }

  /** טקסט מידות לאקספרס: רוחב × גובה בס"מ */
  get step5ExpressDimensionsText(): string {
    const dims = this.getExpressPaperWidthHeightCm();
    if (!dims || this.selectedSystemType === 'plotter') return '';
    const cm = this.translate.instant('printing-table.dimensions-cm');
    const paperName = this.selectedPaperDisplayName;
    return this.translate.instant('edit-instructions.step-5-express-dimensions', { paperName, width: dims.width, height: dims.height, cm });
  }

  /** שם התכנה להצגה בשאלה 5 */
  get softwareDisplayForStep5(): string {
    return this.getSoftwareOptionDisplayLabel(this.selectedSoftwareOption ?? {});
  }

  /** תכנה ללוגיקה שלב 7 – illustrator, photoshop, indesign, autocad, canva, powerpoint, revit, word; אחרת illustrator */
  get step7Software(): 'illustrator' | 'photoshop' | 'indesign' | 'autocad' | 'canva' | 'powerpoint' | 'revit' | 'word' {
    const s = this.selectedSoftware;
    if (s === 'illustrator' || s === 'photoshop' || s === 'indesign' || s === 'autocad' || s === 'canva' || s === 'powerpoint' || s === 'revit' || s === 'word') return s;
    return 'illustrator';
  }

  /** תווית גיליון חדש / קובץ קיים לשלב 7 */
  get step7SheetOrFileLabel(): string {
    if (this.selectedStep6FileType === 'existing') return this.translate.instant('edit-instructions.step-6-existing');
    if (this.selectedStep6FileType === 'skipped') return this.translate.instant('edit-instructions.step-6-new');
    return this.translate.instant('edit-instructions.step-6-new');
  }

  /** פועל לכותרת משנה שלב 7 – "פותחים" בגיליון חדש, "מתאימים" בקובץ קיים */
  get step7SubtitleVerb(): string {
    const key = this.selectedStep6FileType === 'existing' ? 'edit-instructions.step-7-subtitle-verb-adapt' : 'edit-instructions.step-7-subtitle-verb-open';
    return this.translate.instant(key);
  }

  /** השלב הבא שמופיע אחרי שלב 7 (8, או 9, או 10, או 11, או 12 – הראשון שמוצג) */
  get nextStepAfter7(): EditInstructionsStepId {
    if (this.step8Visible) return 8;
    if (this.step9Visible) return 9;
    if (this.step10Visible) return 10;
    if (this.step11Visible) return 11;
    if (this.step12Visible) return 12;
    return 7;
  }

  /** רוחב נייר בס"מ (פלוטר) לשלב 7 */
  get step7PaperWidthCm(): string {
    const paper = this.selectedPaperObject;
    if (!paper || !paper.paperWidth || this.selectedSystemType !== 'plotter') return '';
    return (paper.paperWidth / 1000).toFixed(2);
  }

  /** מידות נייר לשלב 7 (אקספרס/תמונות): width × height בס"מ */
  get step7PaperWidthExpress(): string {
    const dims = this.getExpressPaperWidthHeightCm();
    return dims ? dims.width : '';
  }

  get step7PaperHeightExpress(): string {
    const dims = this.getExpressPaperWidthHeightCm();
    return dims ? dims.height : '';
  }

  /** כותרת שלב 7 – גיליון חדש vs קובץ קיים, עם או בלי תכנה */
  get step7TitleText(): string {
    const isNew = this.selectedStep6FileType !== 'existing';
    const software = this.softwareDisplayForStep5;
    if (isNew && !this.selectedSoftware) return this.translate.instant('edit-instructions.step-7-title-new-no-software');
    if (isNew && this.selectedSoftware) return this.translate.instant('edit-instructions.step-7-title-new-with-software', { software });
    if (!isNew && !this.selectedSoftware) return this.translate.instant('edit-instructions.step-7-title-existing-no-software');
    return this.translate.instant('edit-instructions.step-7-title-existing-with-software', { software });
  }

  /** שלב 6 (גיליון חדש או קובץ קיים) – לא מוצג כשבוחרים "תכנה אחרת" */
  get step6Visible(): boolean {
    return this.selectedSoftware !== 'other';
  }

  /** שלב 7 (איך פותחים גיליון) – לא מוצג כשבוחרים "תכנה אחרת" */
  get step7Visible(): boolean {
    return this.selectedSoftware !== 'other';
  }

  /** שלב 8 (שוליים) מוצג רק כשלא הוגדר סוג הדפסה או שהוגדר פלוטר */
  get step8Visible(): boolean {
    return !this.selectedSystemType || this.selectedSystemType === 'plotter';
  }

  /** שלב 9 (Photoshop) מוצג רק כשלא נבחרה תכנה או נבחרו Illustrator, Indesign, AutoCAD, Revit */
  get step9Visible(): boolean {
    if (!this.selectedSoftware) return true;
    return this.selectedSoftware === 'illustrator' || this.selectedSoftware === 'indesign' || this.selectedSoftware === 'autocad' || this.selectedSoftware === 'revit';
  }

  /** כותרת שלב 9 – עם או בלי שם התכנה */
  get step9TitleText(): string {
    if (!this.selectedSoftware) return this.translate.instant('edit-instructions.step-9-title-no-software');
    return this.translate.instant('edit-instructions.step-9-title-with-software', { software: this.softwareDisplayForStep5 });
  }

  /** שלב 10 (מידה 250 ס"מ): (1) לא נבחר סוג מדפסת או פלוטר, (2) תכנה מהרשימה. (3) לא ענו על 9 / "יש לי Photoshop" – או "אין לי Photoshop" אבל פלוטר + עריכה ב־illustrator/photoshop/indesign/autocad/revit. (4) כשתכנה = AutoCAD ו"אין לי Photoshop" – שאלה 10 לא מוצגת. */
  get step10Visible(): boolean {
    const cond1 = !this.selectedSystemType || this.selectedSystemType === 'plotter';
    const step10Software = !this.selectedSoftware || this.selectedSoftware === 'illustrator' || this.selectedSoftware === 'photoshop' || this.selectedSoftware === 'indesign' || this.selectedSoftware === 'autocad' || this.selectedSoftware === 'revit';
    const cond2 = step10Software;
    const whenNoPhotoshop = this.selectedStep9HasPhotoshop === false && this.selectedSystemType === 'plotter' && (this.selectedSoftware === 'illustrator' || this.selectedSoftware === 'photoshop' || this.selectedSoftware === 'indesign' || this.selectedSoftware === 'autocad' || this.selectedSoftware === 'revit');
    const cond3 = this.selectedStep9HasPhotoshop === null || this.selectedStep9HasPhotoshop === true || whenNoPhotoshop;
    const hideForAutocadNoPhotoshop = this.selectedSoftware === 'autocad' && this.selectedStep9HasPhotoshop === false;
    return cond1 && cond2 && cond3 && !hideForAutocadNoPhotoshop;
  }

  /** האם שלב 10 פתוח ללחיצה (אחרי שענו על שלב 9 אם מוצג, או על שלב 8/7 אחרת) */
  get step10Enabled(): boolean {
    if (this.step9Visible) return this.selectedStep9HasPhotoshop !== null;
    if (this.step8Visible) return this.selectedStep8Ok !== null;
    return this.selectedStep7Ok !== null;
  }

  /** שלב 11 (איך לשמור את הקובץ) – לא מוצג כשבוחרים "תכנה אחרת" */
  get step11Visible(): boolean {
    return this.selectedSoftware !== 'other';
  }

  /** האם שלב 11 פתוח ללחיצה – רק אחרי שענו על 10, 9, 8 או 7 לפי מה שמוצג (לא רלוונטי כש־step11 לא מוצג) */
  get step11Enabled(): boolean {
    if (!this.step11Visible) return false;
    if (this.step10Visible) return this.selectedStep10Size250 !== null;
    if (this.step9Visible) return this.selectedStep9HasPhotoshop !== null;
    if (this.step8Visible) return this.selectedStep8Ok !== null;
    return this.selectedStep7Ok !== null;
  }

  /** האם דרך פוטושופ: true רק אם (1) שאלה 9 מופיעה והתשובה "יש לי Photoshop", (2) שאלה 10 מופיעה והתשובה "שתי המידות קטנות מ-250 ס\"מ" */
  get isViaPhotoshop(): boolean {
    const cond1 = this.step9Visible && this.selectedStep9HasPhotoshop === true;
    const cond2 = this.step10Visible && this.selectedStep10Size250 === false;
    return cond1 && cond2;
  }

  /** האם קובץ גדול: true רק אם פלוטר + שאלה 10 מופיעה והתשובה "אחת המידות גדולה מ-250 ס\"מ" */
  get isLargeFile(): boolean {
    return this.selectedSystemType === 'plotter' && this.step10Visible && this.selectedStep10Size250 === true;
  }

  /** הצג PDF בשלב 11 רק עבור Illustrator/Indesign/AutoCAD: תכנה אחת מהם + תשובה 9 "יש לי Photoshop" + (תשובה 10 "שתי המידות קטנות" או סוג מדפסת לא פלוטר) */
  get showPdfForAiIdAc(): boolean {
    const isAiIdAc = this.selectedSoftware === 'illustrator' || this.selectedSoftware === 'indesign' || this.selectedSoftware === 'autocad';
    const q9YesPhotoshop = this.selectedStep9HasPhotoshop === true;
    const q10SmallOrNotPlotter = this.selectedStep10Size250 === false || this.selectedSystemType !== 'plotter';
    return !!isAiIdAc && q9YesPhotoshop && q10SmallOrNotPlotter;
  }

  /** שלב 12 (PDF דרך Photoshop) מוצג כשמתקיימים: תכנה, Photoshop, מידה 250 */
  get step12Visible(): boolean {
    const cond2 = !this.selectedSoftware || this.selectedSoftware === 'illustrator' || this.selectedSoftware === 'indesign' || this.selectedSoftware === 'autocad' || this.selectedSoftware === 'revit';
    const cond3 = this.selectedStep9HasPhotoshop === null || this.selectedStep9HasPhotoshop === true;
    const cond4 = this.selectedStep10Size250 === null || this.selectedStep10Size250 === false;
    return cond2 && cond3 && cond4;
  }

  /** האם שלב 12 פתוח ללחיצה – רק אחרי שענו על שלב 11 */
  get step12Enabled(): boolean {
    return this.selectedStep11Ok !== null;
  }

  /** שלב 13 (מה לבדוק לפני הדפסה) – תמיד מוצג */
  get step13Visible(): boolean {
    return true;
  }

  /** האם שלב 13 פתוח ללחיצה – אחרי שענו על שלב 12 (אם מוצג) או על שלב 11 (אם מוצג), או אחרי 8/9/10/12 כששלב 11 מוסתר (תכנה אחרת) */
  get step13Enabled(): boolean {
    if (this.step12Visible) return this.selectedStep12Ok !== null;
    if (this.step11Visible) return this.selectedStep11Ok !== null;
    if (this.selectedSoftware === 'other' && this.step8Visible) {
      if (this.selectedStep8Ok === null) return false;
      if (this.step9Visible && this.selectedStep9HasPhotoshop === null) return false;
      if (this.step10Visible && this.selectedStep10Size250 === null) return false;
      if (this.step12Visible && this.selectedStep12Ok === null) return false;
      return true;
    }
    return false;
  }

  /** נתיב תמונה ראשית לשלב 7 (לא בשימוש ב-Revit – יש בלוק נפרד) */
  get step7MainImagePath(): string {
    const sw = this.step7Software;
    const isNew = this.selectedStep6FileType !== 'existing';
    if (sw === 'illustrator') return 'assets/images/edit/openFile-Illustrator.jpg';
    if (sw === 'photoshop') return isNew ? 'assets/images/edit/openFile-Photoshop-new.jpg' : 'assets/images/edit/openFile-Photoshop-exist.jpg';
    if (sw === 'indesign') return isNew ? 'assets/images/edit/openFile-Indesign-new.jpg' : 'assets/images/edit/openFile-Indesign-exist.jpg';
    if (sw === 'autocad') return 'assets/images/edit/openFile-AutoCAD-6.jpg';
    if (sw === 'canva') return isNew ? 'assets/images/edit/openFile-Canva-new-3.jpg' : 'assets/images/edit/openFile-Canva-exist-3.jpg';
    if (sw === 'powerpoint') return 'assets/images/edit/openFile-PowerPoint-3.jpg';
    if (sw === 'revit') return 'assets/images/edit/openFile-Revit-new-2.jpg'; /* לא מוצג – Revit משתמש בבלוק תמונות נפרד */
    if (sw === 'word') return 'assets/images/edit/openFile-Word-2.jpg'; /* לא מוצג – Word משתמש בבלוק תמונות נפרד */
    return 'assets/images/edit/openFile-Illustrator.jpg';
  }

  /** כתובת אתר Canva */
  readonly canvaWebsiteUrl = 'https://www.canva.com/';

  /** נתיב תמונה ראשונה ב-AutoCAD – גיליון חדש vs קובץ קיים */
  get step7AutocadFirstImagePath(): string {
    const isNew = this.selectedStep6FileType !== 'existing';
    return isNew ? 'assets/images/edit/openFile-AutoCAD-new-1.jpg' : 'assets/images/edit/openFile-AutoCAD-exist-1.jpg';
  }

  /** נתיב תמונה שנייה ב-Revit – גיליון חדש vs קובץ קיים */
  get step7RevitSecondImagePath(): string {
    const isNew = this.selectedStep6FileType !== 'existing';
    return isNew ? 'assets/images/edit/openFile-Revit-new-2.jpg' : 'assets/images/edit/openFile-Revit-exist-2.jpg';
  }

  /** תווית כפתור "לאחר מכן לוחצים X" – Create בגיליון חדש ב-Illustrator/Indesign/Photoshop, אחרת OK */
  get step7ThenButtonLabel(): string {
    const isNew = this.selectedStep6FileType !== 'existing';
    if ((this.step7Software === 'illustrator' || this.step7Software === 'indesign' || this.step7Software === 'photoshop') && isNew) return 'Create';
    return 'OK';
  }

  constructor(
    private directionService: DirectionService,
    private authService: AuthService,
    private router: Router,
    private translate: TranslateService,
    private clustersService: ClustersService,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    this.loadClusters();
    this.rolesSubscription = this.authService.roles$.subscribe((roles) => {
      this.roles = roles ?? [];
    });

    this.userNameSubscription = this.authService.userName$.subscribe((name) => {
      this.userName = name ?? '';
    });

    this.directionSubscription = this.directionService.direction$.subscribe(
      (direction) => {
        this.isRTL = direction === 'rtl';
      }
    );

    this.directionService.isDarkMode$.subscribe((isDarkMode) => {
      this.isDarkMode = isDarkMode;
    });
  }

  /** תיקון viewport באנדרואיד – כשגובה המסך משתנה (Chrome), מחזיר גלילה למניעת רווח מתחת לכפתורים */
  @HostListener('window:resize')
  onWindowResize(): void {
    this.fixViewportOnMobile();
  }

  ngAfterViewInit(): void {
    this.lastViewportHeight = window.visualViewport?.height ?? window.innerHeight;
    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.visualViewportResizeHandler);
    }
  }

  private visualViewportResizeHandler = (): void => {
    this.fixViewportOnMobile();
  };

  /** כמו בטבלת ההדפסה: כשגובה התצוגה משתנה באנדרואיד – מחזיר window ל-top ומקבע scroll של הקונטיינר */
  private fixViewportOnMobile(): void {
    if (window.innerWidth > 767.98) return;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    if (this.lastViewportHeight > 0 && Math.abs(vh - this.lastViewportHeight) > 50) {
      window.scrollTo(0, 0);
      requestAnimationFrame(() => {
        const el = this.scrollContainerRef?.nativeElement;
        if (el) {
          el.scrollTop = Math.min(el.scrollTop, Math.max(0, el.scrollHeight - el.clientHeight));
        }
      });
    }
    this.lastViewportHeight = vh;
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.visualViewportResizeHandler);
    }
    this.directionSubscription?.unsubscribe();
    this.rolesSubscription?.unsubscribe();
    this.userNameSubscription?.unsubscribe();
  }

  private loadClusters() {
    this.isLoading = true;
    this.clustersLoadCompleted = false;
    this.clustersService.getClustersForEditInstructions().subscribe({
      next: (clusters) => {
        this.clusters = clusters ?? [];
        this.isLoading = false;
        this.clustersLoadCompleted = true;
      },
      error: (err) => {
        console.error('Failed to load clusters for edit instructions', err);
        this.clusters = [];
        this.isLoading = false;
      },
    });
  }

  getClusterDisplayName(serialName: string): string {
    if (!serialName) return '';
    const key = 'choose-branch.naming.' + serialName;
    const t = this.translate.instant(key);
    return t !== key ? t : serialName;
  }

  /** תווית להצגה לאפשרות תכנה – hardcoded או מתרגום */
  getSoftwareOptionDisplayLabel(opt: { label?: string; labelKey?: string }): string {
    if (opt.label) return opt.label;
    if (opt.labelKey) return this.translate.instant(opt.labelKey);
    return '';
  }

  /** מעבר לשאלה – כשבוחרים שאלה מוקדמת יותר, מאפס את התשובה שלה ואת כל התשובות שאחריה */
  goToStep(stepId: EditInstructionsStepId) {
    if (stepId !== 12) this.expandedAntiAliasedExplain = false;
    if (stepId !== 7) this.expandedStep7ProblemExplain = false;
    if (stepId !== 8) this.expandedStep8ProblemExplain = false;
    if (stepId !== 11) this.expandedStep11ProblemExplain = false;
    if (stepId !== 12) this.expandedStep12ProblemExplain = false;
    if (stepId !== 13) this.expandedStep13ProblemExplain = false;
    this.currentStep = stepId;
    if (stepId <= 1) {
      this.selectedSystemType = null;
      this.selectedClusterSerialName = null;
      this.selectedSoftware = null;
      this.selectedPaperType = null;
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
      this.expandedExplainOption = null;
      this.expandedPaperExplain = null;
    }
    if (stepId <= 2) {
      this.selectedClusterSerialName = null;
      this.selectedSoftware = null;
      this.selectedPaperType = null;
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
      this.expandedPaperExplain = null;
    }
    if (stepId <= 3) {
      this.selectedSoftware = null;
      this.selectedPaperType = null;
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
      this.expandedPaperExplain = null;
    }
    if (stepId <= 4) {
      this.selectedPaperType = null;
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId <= 5) {
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId <= 6) {
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId <= 7) {
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId <= 8) {
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId <= 9) {
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId <= 10) {
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId <= 11) {
      this.selectedStep12Ok = null;
    }
    this.scheduleScrollToStep(stepId);
  }

  /** גלילה חלקה לתחילת הפאנל של השלב – אחרי פתיחת שאלה חדשה.
   * כמו בטבלת ההדפסה: scrollIntoView + אחרי הגלילה מחזירים את window ל-top ומקבעים scroll
   * כדי למנוע רווח מתחת לכפתורים והיעלמות ה-toolbar באנדרואיד. */
  private scrollToStep(stepId: EditInstructionsStepId): void {
    const el = document.getElementById('edit-instructions-step-' + stepId);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // כמו בטבלת ההדפסה: גלילה נוספת בסיום – מחזיר window ל-top ומקבע scroll של הקונטיינר
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      requestAnimationFrame(() => {
        const container = this.scrollContainerRef?.nativeElement;
        if (container) {
          container.scrollTop = Math.min(container.scrollTop, Math.max(0, container.scrollHeight - container.clientHeight));
        }
      });
    }, 600);
  }

  private scheduleScrollToStep(stepId: EditInstructionsStepId): void {
    setTimeout(() => this.scrollToStep(stepId), 100);
  }

  onSelectSystem(type: EditInstructionsSystemType) {
    this.selectedSystemType = type;
    this.selectedClusterSerialName = null;
    this.selectedSoftware = null;
    this.selectedPaperType = null;
    this.selectedStep5KnowsDimensions = null;
    this.selectedStep6FileType = null;
    this.selectedStep7Ok = null;
    this.selectedStep8Ok = null;
    this.selectedStep9HasPhotoshop = null;
    this.selectedStep10Size250 = null;
    this.selectedStep11Ok = null;
    this.currentStep = 2;
    this.scheduleScrollToStep(2);
  }

  onSelectSoftware(value: EditInstructionsSoftwareType) {
    this.selectedSoftware = value;
    this.currentStep = 4;
    this.scheduleScrollToStep(4);
  }

  onSelectPaper(paperType: string) {
    this.selectedPaperType = paperType;
    this.currentStep = 5;
    this.scheduleScrollToStep(5);
  }

  onSelectStep5Answer(knows: boolean) {
    this.selectedStep5KnowsDimensions = knows;
    if (knows) {
      this.selectedStep6FileType = 'skipped';
      this.selectedStep7Ok = 'skipped';
      this.currentStep = this.nextStepAfter7;
      this.scheduleScrollToStep(this.currentStep);
    } else {
      this.currentStep = 6;
      this.scheduleScrollToStep(6);
    }
  }

  /** לחיצה על "נמשיך לשלב הבא" בשלב 5 כשנבחרה תכנה אחרת – מדלג על שלבים 6,7 ועובר לשלב 8 */
  onStep5OtherContinue() {
    this.selectedStep5KnowsDimensions = true;
    this.selectedStep6FileType = 'skipped';
    this.selectedStep7Ok = 'skipped';
    this.currentStep = 8;
    this.scheduleScrollToStep(8);
  }

  onSelectStep6Answer(fileType: 'new' | 'existing') {
    this.selectedStep6FileType = fileType;
    this.currentStep = 7;
    this.scheduleScrollToStep(7);
  }

  onSelectStep7Answer(ok: boolean) {
    this.expandedStep7ProblemExplain = false;
    this.selectedStep7Ok = ok;
    if (ok && this.step8Visible) {
      this.currentStep = 8;
    } else if (ok && this.step9Visible) {
      this.currentStep = 9;
    } else if (ok && this.step10Visible) {
      this.currentStep = 10;
    } else if (ok && this.step11Visible) {
      this.currentStep = 11;
    } else if (ok && this.step12Visible) {
      this.currentStep = 12;
    }
    this.scheduleScrollToStep(this.currentStep);
  }

  onSelectStep8Answer(ok: boolean) {
    this.expandedStep8ProblemExplain = false;
    this.selectedStep8Ok = ok;
    if (ok && this.step9Visible) {
      this.currentStep = 9;
    } else if (ok && this.step10Visible) {
      this.currentStep = 10;
    } else if (ok && this.step11Visible) {
      this.currentStep = 11;
    } else if (ok && this.step12Visible) {
      this.currentStep = 12;
    } else if (ok && this.step13Visible) {
      this.currentStep = 13;
    }
    this.scheduleScrollToStep(this.currentStep);
  }

  onSelectStep9Answer(hasPhotoshop: boolean) {
    this.selectedStep9HasPhotoshop = hasPhotoshop;
    if (this.selectedSoftware === 'autocad' && hasPhotoshop === false) {
      this.selectedStep10Size250 = null;
    }
    if (this.step10Visible) {
      this.currentStep = 10;
    } else if (this.step11Visible) {
      this.currentStep = 11;
    } else if (this.step12Visible) {
      this.currentStep = 12;
    }
    this.scheduleScrollToStep(this.currentStep);
  }

  onSelectStep10Answer(oneDimensionOver250: boolean) {
    this.selectedStep10Size250 = oneDimensionOver250;
    this.currentStep = 11;
    this.scheduleScrollToStep(11);
  }

  onSelectStep11Answer(ok: boolean) {
    this.expandedStep11ProblemExplain = false;
    this.selectedStep11Ok = ok;
    if (ok && this.step12Visible) {
      this.currentStep = 12;
    } else if (ok && this.step13Visible) {
      this.currentStep = 13;
    }
    this.scheduleScrollToStep(this.currentStep);
  }

  onSelectStep12Answer(ok: boolean) {
    this.expandedStep12ProblemExplain = false;
    this.selectedStep12Ok = ok;
    if (ok && this.step13Visible) {
      this.currentStep = 13;
    }
    this.scheduleScrollToStep(this.currentStep);
  }

  toggleExplain(type: EditInstructionsSystemType) {
    this.expandedExplainOption = this.expandedExplainOption === type ? null : type;
  }

  togglePaperExplain(paperType: string) {
    this.expandedPaperExplain = this.expandedPaperExplain === paperType ? null : paperType;
  }

  toggleAntiAliasedExplain() {
    this.expandedAntiAliasedExplain = !this.expandedAntiAliasedExplain;
  }

  toggleStep7ProblemExplain() {
    this.expandedStep7ProblemExplain = !this.expandedStep7ProblemExplain;
  }

  toggleStep8ProblemExplain() {
    this.expandedStep8ProblemExplain = !this.expandedStep8ProblemExplain;
  }

  toggleStep11ProblemExplain() {
    this.expandedStep11ProblemExplain = !this.expandedStep11ProblemExplain;
  }

  toggleStep12ProblemExplain() {
    this.expandedStep12ProblemExplain = !this.expandedStep12ProblemExplain;
  }

  toggleStep13ProblemExplain() {
    this.expandedStep13ProblemExplain = !this.expandedStep13ProblemExplain;
  }

  /** פתיחת וואטסאפ עם הודעה קבועה להסבר עריכה (כמו ב-helpers) */
  openWhatsAppEditInstructions() {
    const phoneNumber = '97233746962';
    const message = encodeURIComponent('Editing-Instructions-At-Eazix');
    const url = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(url, '_blank');
  }

  /** מידות תמונות (תמונות S/M/L) בס"מ – API לא מחזיר paperWidth/paperHeight */
  private static readonly PICS_DIMENSIONS_CM: Record<string, { width: number; height: number }> = {
    SM: { width: 10.16, height: 15.24 },
    MD: { width: 12.7, height: 17.78 },
    LG: { width: 15.24, height: 20.32 },
    'CY-SM': { width: 10.16, height: 15.24 },
    'CY-MD': { width: 12.7, height: 17.78 },
    'CY-LG': { width: 15.24, height: 20.32 },
    'תמונה קטנה (S)': { width: 10.16, height: 15.24 },
    'תמונה בינונית (M)': { width: 12.7, height: 17.78 },
    'תמונה גדולה (L)': { width: 15.24, height: 20.32 },
  };

  /** מיפוי שם בעברית מממשק API למפתח תרגום */
  private static readonly PAPER_NAME_TO_KEY: Record<string, string> = {
    'פשוט': 'PLAIN',
    'מצופה': 'COATED',
    'פוטו': 'PHOTO',
  };

  /** מחזיר מפתח תרגום לנייר – paperTypes מה-API עם name (עברית או קוד כמו A4) */
  getPaperTypeName(paper: any): string {
    if (!paper || !paper.name) return '';
    const name = String(paper.name).trim();
    return EditInstructionsComponent.PAPER_NAME_TO_KEY[name] || name;
  }

  getPaperExplanationKey(paper: any): string {
    const paperType = this.getPaperTypeName(paper);
    return 'paper-type.description.' + paperType;
  }

  /** האם שלב 9 פתוח ללחיצה (אחרי שענו על שלב 8 אם מוצג, או אחרי שלב 7 אם שלב 8 לא מוצג) */
  get step9Enabled(): boolean {
    if (this.step8Visible) return this.selectedStep8Ok !== null;
    return this.selectedStep7Ok !== null;
  }

  onStepHeaderClick(stepId: EditInstructionsStepId) {
    if (stepId === 2 && !this.selectedSystemType) return;
    if (stepId === 3 && !this.selectedClusterSerialName) return;
    if (stepId === 4 && !this.selectedSoftware) return;
    if (stepId === 5 && !this.selectedPaperType) return;
    if (stepId === 6 && this.selectedStep5KnowsDimensions === null) return;
    if (stepId === 7 && this.selectedStep6FileType === null) return;
    if (stepId === 8 && this.selectedStep7Ok === null) return;
    if (stepId === 9 && !this.step9Enabled) return;
    if (stepId === 10 && !this.step10Enabled) return;
    if (stepId === 11 && !this.step11Enabled) return;
    if (stepId === 12 && !this.step12Enabled) return;
    if (stepId === 13 && !this.step13Enabled) return;
    this.goToStep(stepId);
  }

  /** לחיצה על אייקון עריכה – פותח את השלב לשינוי, בלי לאפס את הבחירה בשלב הנוכחי */
  onEditAnswer(stepId: EditInstructionsStepId) {
    this.currentStep = stepId;
    if (stepId === 1) {
      this.selectedSystemType = null;
      this.selectedClusterSerialName = null;
      this.selectedSoftware = null;
      this.selectedPaperType = null;
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
      this.expandedExplainOption = null;
      this.expandedPaperExplain = null;
    }
    if (stepId === 2) {
      this.selectedClusterSerialName = null;
      this.selectedSoftware = null;
      this.selectedPaperType = null;
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
      this.expandedPaperExplain = null;
    }
    if (stepId === 3) {
      this.selectedSoftware = null;
      this.selectedPaperType = null;
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
      this.expandedPaperExplain = null;
    }
    if (stepId === 4) {
      this.selectedPaperType = null;
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId === 5) {
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId === 6) {
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId === 7) {
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId === 8) {
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId === 9) {
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId === 10) {
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId === 11) {
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
    }
    if (stepId === 12) {
      this.selectedStep12Ok = null;
    }
    this.scheduleScrollToStep(stepId);
  }

  onSelectCluster(cluster: any) {
    const isSame = this.selectedClusterSerialName === cluster.serial_name;
    this.selectedClusterSerialName = isSame ? null : cluster.serial_name;
    if (!isSame && this.selectedClusterSerialName) {
      this.selectedSoftware = null;
      this.selectedPaperType = null;
      this.selectedStep5KnowsDimensions = null;
      this.selectedStep6FileType = null;
      this.selectedStep7Ok = null;
      this.selectedStep8Ok = null;
      this.selectedStep9HasPhotoshop = null;
      this.selectedStep10Size250 = null;
      this.selectedStep11Ok = null;
      this.selectedStep12Ok = null;
      this.currentStep = 3;
      this.scheduleScrollToStep(3);
    }
  }

  /** כמו "צור הזמנה חדשה" ב־NAVBAR; אם המשתמש לא מחובר – פותח חלונית התחברות */
  goToNewOrder() {
    if (!this.userName || this.userName === '') {
      this.dialogService.onOpenLoginDialog('', '');
      return;
    }
    const printingService = localStorage.getItem('printingService');
    const branch = localStorage.getItem('branch');
    if (
      printingService &&
      printingService !== 'null' &&
      printingService !== '' &&
      branch &&
      branch !== 'null' &&
      branch !== ''
    ) {
      this.dialogService.onOpenRightPlaceDialog();
    } else if (branch && branch !== 'null' && branch !== '') {
      this.router.navigate(['/branch']);
    } else {
      this.router.navigate(['/']);
    }
  }
}
